'use strict';

import AddSearchUI from 'addsearch-search-ui';
import { AUTOCOMPLETE_TEMPLATE, toggleAutocomplete } from './helper/autocomplete.js';
import { getAddSearchClient } from './helper/client.js';
import { mpRegisterHandlebarsHelper } from './helper/handlebars.js';
import { getContainerID, selectors, getLabels, isInfiniteScroll } from './helper/initialization.js';
import { getSearchInputTemplate } from './helper/input.js';
import { registerEventListener } from './helper/listeners.js';
import { getSegmentTemplate } from './helper/segments.js';

const initSearch = (searchEl) => {
	/**
	 * -- How it works --
	 *
	 * There are basically two search layouts. Infinite scroll and segmented search results.
	 * The search always needs a client. A base client (const addSearchClient) serves to initialize the search UI object.
	 *
	 * If the layout is infinite the basic AddSearch client will be used for the search results to enable infinite scroll.
	 * If the layout is segmented search results two new clients will be created with the appropriate filter settings.
	 */

	const idSuffix = searchEl.getAttribute('data-id-suffix');

	// Create basic client and searchUI instance
	const addSearchClient = getAddSearchClient(getContainerID(idSuffix).searchResultsMain);

	let conf = {};
	if (isInfiniteScroll(searchEl)) {
		conf.baseFilters = JSON.parse(searchEl.querySelector(`#${getContainerID(idSuffix).searchResultsMain}`).getAttribute('data-filters'));
	}

	// Analytics Callback
	conf.analyticsCallback = (search) => {
		// TODO: Track search events right
		// Google Analytics
		if (window.ga) {
			const trackingId = ga.getAll()[0].get('trackingId');
			if (trackingId) {
				ga('create', trackingId);
				ga('send', 'pageview', `/?s=${search.keyword}`);
			}
		}

		// Matomo
		if (window._paq) {
			window._paq.push(['trackSiteSearch', search.keyword, false, search.numberOfResults]);
		}
	};

	const addSearchUI = new AddSearchUI(addSearchClient, conf);

	mpRegisterHandlebarsHelper(searchEl, addSearchUI);

	// Component: Search Input Field
	let searchInputTemplate = getSearchInputTemplate();
	addSearchUI.searchField({
		containerId: getContainerID(idSuffix).searchSearchfield,
		placeholder: getLabels('input'),
		searchAsYouType: true,
		template: searchInputTemplate,
	});

	// Check if infinite scroll is activated
	if (isInfiniteScroll(searchEl)) {
		/**
		 * Single Result Segment
		 *
		 * Working with .searchResults() function
		 */

		addSearchUI.searchResults({
			containerId: getContainerID(idSuffix).searchResultsMain,
			template: getSegmentTemplate(idSuffix, getContainerID(idSuffix).searchResultsMain, false),
			template_noresults: '<span></span>',
		});

		// Component: Load More (Infinite Scroll)
		addSearchUI.loadMore({
			containerId: getContainerID(idSuffix).searchInfinite,
			type: AddSearchUI.LOAD_MORE_TYPE.INFINITE_SCROLL,
			infiniteScrollElement: searchEl.querySelector(selectors.searchResults),
		});
	} else {
		/**
		 * Result Segments
		 *
		 * Working with .segmentedSearchResults() function
		 */
		let template;

		// Main segment
		var mainSegmentClient = getAddSearchClient(getContainerID(idSuffix).searchResultsMain);
		template = getSegmentTemplate(idSuffix, getContainerID(idSuffix).searchResultsMain);

		addSearchUI.segmentedSearchResults({
			containerId: getContainerID(idSuffix).searchResultsMain,
			template: template,
			client: mainSegmentClient,
		});

		// Additional segment
		if (searchEl.querySelector('#' + getContainerID(idSuffix).searchResultsAdditional)) {
			let additionalSegmentClient = getAddSearchClient(getContainerID(idSuffix).searchResultsAdditional);
			template = getSegmentTemplate(idSuffix, getContainerID(idSuffix).searchResultsAdditional);

			addSearchUI.segmentedSearchResults({
				containerId: getContainerID(idSuffix).searchResultsAdditional,
				template: template,
				client: additionalSegmentClient,
			});
		}
	}

	// Component: Autocomplete
	if (searchEl.querySelector(selectors.searchAutocomplete)) {
		let autocompleteConfig = {
			containerId: getContainerID(idSuffix).searchAutocomplete,
			sources: [
				{
					type: AddSearchUI.AUTOCOMPLETE_TYPE.SUGGESTIONS,
				},
			],
			template: AUTOCOMPLETE_TEMPLATE,
		};

		// Offset (Delay)
		if (searchEl.querySelector(selectors.searchAutocomplete).getAttribute('data-delay')) {
			autocompleteConfig.onShow = () => {
				toggleAutocomplete(searchEl);
			};
		}

		addSearchUI.autocomplete(autocompleteConfig);
		addSearchClient.setSuggestionsSize(searchEl.querySelector(selectors.searchAutocomplete).getAttribute('data-count'));
	}

	// Register all event listeners
	registerEventListener(searchEl);

	// Start after all components are added
	addSearchUI.start();
};

const searchElements = document.querySelectorAll(selectors.searchContainer);
searchElements.forEach((searchEl) => {
	initSearch(searchEl);
});
